import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCatalogListings,
  deleteCatalogListing,
  updateCatalogListing,
  CatalogListing,
} from '@/services/catalog';
import { CatalogListingModal } from '@/features/catalog/CatalogListingModal';
import { TableSkeleton } from '@/components/common/Skeleton';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { SearchInput } from '@/components/common/SearchInput';
import { parseDatabaseError } from '@/lib/errors';
import {
  Store,
  Plus,
  Edit2,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  Copy,
  Check,
  Laptop as LaptopIcon,
  Package,
  Globe,
} from 'lucide-react';

export const CatalogManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingListing, setEditingListing] = useState<CatalogListing | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const { data: listings = [], isLoading, error, refetch } = useQuery({
    queryKey: ['catalog_listings'],
    queryFn: fetchCatalogListings,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCatalogListing,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalog_listings'] });
    },
  });

  const togglePublishMutation = useMutation({
    mutationFn: ({ id, is_published }: { id: string; is_published: boolean }) =>
      updateCatalogListing(id, { is_published }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalog_listings'] });
    },
  });

  const filteredListings = listings.filter((l) => {
    if (typeFilter && l.listing_type !== typeFilter) return false;
    if (statusFilter === 'published' && !l.is_published) return false;
    if (statusFilter === 'draft' && l.is_published) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (
        l.title.toLowerCase().includes(s) ||
        l.slug.toLowerCase().includes(s) ||
        (l.brand && l.brand.toLowerCase().includes(s)) ||
        (l.model && l.model.toLowerCase().includes(s)) ||
        (l.badge && l.badge.toLowerCase().includes(s))
      );
    }
    return true;
  });

  const handleCopyLink = (slug: string) => {
    const url = `${window.location.origin}/shop/${slug}`;
    void navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const handleDelete = (listing: CatalogListing) => {
    if (confirm(`Delete catalog listing "${listing.title}"? This cannot be undone.`)) {
      deleteMutation.mutate(listing.id);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-brand-black flex items-center space-x-2">
            <Store className="w-5 h-5 text-brand-green" />
            <span>Public Product Catalogue</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            Manage customer-facing store listings, promotional pricing, and WhatsApp showcase pages.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <a
            href="/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 border border-brand-border text-xs font-semibold rounded-md text-brand-black hover:bg-brand-bg transition-colors"
          >
            <Globe className="w-3.5 h-3.5 text-brand-green" />
            <span>View Public Store</span>
            <ExternalLink className="w-3 h-3 text-brand-muted" />
          </a>
          <button
            type="button"
            onClick={() => {
              setEditingListing(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Listing</span>
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search listings by title, slug, brand, model..."
          />
        </div>
        <div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded-md text-xs font-medium text-brand-black"
          >
            <option value="">All Listing Types</option>
            <option value="laptop_group">Laptop Groups</option>
            <option value="accessory">Accessories / Parts</option>
          </select>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded-md text-xs font-medium text-brand-black"
          >
            <option value="">All Visibility</option>
            <option value="published">Published Only</option>
            <option value="draft">Draft / Hidden</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Table */}
      <div className="bg-white rounded-lg border border-brand-border shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3">Listing Title & Slug</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Type / Category</th>
                <th className="text-left px-4 py-3">Pricing</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border text-xs">
              {isLoading ? (
                <TableSkeleton rows={6} cols={5} />
              ) : filteredListings.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-brand-muted">
                    No catalog listings found. Click "New Listing" to publish a product to the public store.
                  </td>
                </tr>
              ) : (
                filteredListings.map((listing) => (
                  <tr key={listing.id} className="hover:bg-brand-bg/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {listing.listing_type === 'laptop_group' ? (
                          <div className="p-1.5 bg-green-50 text-brand-green rounded shrink-0">
                            <LaptopIcon className="w-4 h-4" />
                          </div>
                        ) : (
                          <div className="p-1.5 bg-blue-50 text-blue-600 rounded shrink-0">
                            <Package className="w-4 h-4" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center space-x-1.5 flex-wrap">
                            <p className="font-bold text-brand-black">{listing.title}</p>
                            {listing.badge && (
                              <span className="px-1.5 py-0.2 bg-amber-100 text-amber-900 text-[9px] font-bold rounded">
                                {listing.badge}
                              </span>
                            )}
                            {listing.is_featured && (
                              <span className="px-1.5 py-0.2 bg-purple-100 text-purple-900 text-[9px] font-bold rounded">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] font-mono text-brand-muted mt-0.5">
                            /shop/{listing.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="capitalize font-semibold text-brand-black">
                        {listing.listing_type === 'laptop_group' ? 'Laptop Model Group' : 'Accessory'}
                      </span>
                      {listing.brand && (
                        <p className="text-[11px] text-brand-muted">
                          {listing.brand} {listing.model}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {listing.display_price_override != null ? (
                        <span className="font-bold text-brand-green">
                          Rs. {listing.display_price_override.toLocaleString('en-PK')}
                        </span>
                      ) : (
                        <span className="text-brand-muted italic">Dynamic Stock Range</span>
                      )}
                      {listing.original_price && (
                        <span className="text-[10px] text-brand-muted line-through block">
                          Rs. {listing.original_price.toLocaleString('en-PK')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          togglePublishMutation.mutate({
                            id: listing.id,
                            is_published: !listing.is_published,
                          })
                        }
                        className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-colors ${
                          listing.is_published
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        title="Click to toggle publish status"
                      >
                        {listing.is_published ? (
                          <>
                            <Eye className="w-3 h-3" />
                            <span>Published</span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3" />
                            <span>Draft</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => handleCopyLink(listing.slug)}
                          className="p-1.5 text-brand-muted hover:text-brand-black hover:bg-brand-bg rounded"
                          title="Copy public link"
                        >
                          {copiedSlug === listing.slug ? (
                            <Check className="w-3.5 h-3.5 text-brand-green" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <a
                          href={`/shop/${listing.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-brand-muted hover:text-brand-black hover:bg-brand-bg rounded"
                          title="View public page"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingListing(listing);
                            setModalOpen(true);
                          }}
                          className="p-1.5 text-brand-muted hover:text-brand-green hover:bg-brand-bg rounded"
                          title="Edit listing"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(listing)}
                          className="p-1.5 text-brand-muted hover:text-brand-red hover:bg-brand-bg rounded"
                          title="Delete listing"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <CatalogListingModal
          isOpen={modalOpen}
          listing={editingListing}
          onClose={() => {
            setModalOpen(false);
            setEditingListing(null);
          }}
          onSuccess={() => {
            void refetch();
          }}
        />
      )}
    </div>
  );
};
